import {
  KatButton,
  KatCheckbox,
  KatFileUpload,
  KatTextarea,
} from '@amzn/katal-react';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { CorrespondenceTextEditorConstants } from 'src/constants/ProjectConstants';
import Uploader from '../FileUpload/Uploader';
import { emitClickMetrics, MetricsName } from 'src/metrics';
import { TranslationContext } from 'src/state/providers/TranslationProvider';
import { AuthContext } from 'src/state/providers/AuthProvider';
import {
  ALLOWED_FILE_TYPES_FOR_ATTACHMENTS,
  CONFIG_TYPE,
  FILE_EXTENSION,
  UI_CLIENTS,
} from 'src/constants/GlobalConstants';
import { fetchFromAppConfig } from 'src/config-store/ConfigFetcher';

const TextEditingArea = styled.div`
  kat-textarea {
    width: 100%;
    max-width: 100%;
    --height: 20vh;
    --background: ${({ theme }) => theme.colors.White};
    --border-color: ${({ theme }) => theme.colors.Tarpon};
    --font-size: ${({ theme }) => theme.fontSize.m};
  }
`;

export const ElementSection = styled.section`
  padding-top: 5px;
  padding-bottom: 5px;
`;

export const EditorFooter = styled.div`
  display: flex;
  flex-direction: row-reverse;
`;

export interface TextEditorType extends KatFileUpload.Props {
  editorContent: string;
  updateEditorContent: (value: string) => void;
  onEditorSubmit: Function;
  disableSubmitButton: boolean;
  displayOnPublicPortal?: boolean;
  setDisplayOnPublicPortal?: (value: boolean) => void;
  attachmentValidationError?: string;
}

export function getAllowedFileTypes(
  accountId: string,
  clientId: UI_CLIENTS,
): FILE_EXTENSION[] {
  const allowedTypes = fetchFromAppConfig(
    accountId,
    clientId,
    CONFIG_TYPE.PROPERTIES,
    ALLOWED_FILE_TYPES_FOR_ATTACHMENTS,
  );
  return allowedTypes;
}

const TextEditor: React.FC<TextEditorType> = ({
  editorContent,
  updateEditorContent,
  onEditorSubmit,
  onFilesAttached,
  onFilesRemoved,
  onFilesReplaced,
  fileStates,
  disableSubmitButton,
  displayOnPublicPortal = false,
  setDisplayOnPublicPortal = () => {},
  attachmentValidationError,
}) => {
  const { communicationPageBundle } = useContext(TranslationContext);
  const { preferredAccountId, clientId } = useContext(AuthContext);
  const allowedFileTypes = getAllowedFileTypes(preferredAccountId, clientId);

  return (
    <TextEditingArea>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          emitClickMetrics(
            MetricsName.CORRESPONDENCE_TAB,
            'AddCorrespondence.Button.Clicked',
          );
          onEditorSubmit();
        }}
      >
        <ElementSection data-testid="correspondence-element-section">
          <KatTextarea
            data-testid="correspondence-text-editor"
            label={communicationPageBundle.getMessage(
              CorrespondenceTextEditorConstants.ADD_COMMENT,
            )}
            value={editorContent}
            onInput={(e) => updateEditorContent(e.currentTarget.value)}
            maxlength={CorrespondenceTextEditorConstants.MAX_COMMENT_LENGTH}
          ></KatTextarea>
        </ElementSection>
        {UI_CLIENTS.VICI == clientId && (
          <ElementSection>
            <KatCheckbox
              label={communicationPageBundle.getMessage(
                CorrespondenceTextEditorConstants.SHOW_TO_EXTERNAL_USERS,
              )}
              checked={displayOnPublicPortal}
              onChange={(e: KatCheckbox.ChangeEvent) => {
                setDisplayOnPublicPortal(e.detail.checked);
              }}
            />
          </ElementSection>
        )}
        <ElementSection data-testid="correspondence-element-section">
          <Uploader
            variant="small"
            onFilesAttached={onFilesAttached}
            onFilesRemoved={onFilesRemoved}
            onFilesReplaced={onFilesReplaced}
            fileStates={fileStates}
            allowedFiles={allowedFileTypes}
            errorMessage={attachmentValidationError || undefined}
          />
        </ElementSection>
        <ElementSection data-testid="correspondence-element-section">
          <EditorFooter data-testid="correspondence-editor-footer">
            <KatButton
              data-testid="correspondence-submit-button"
              type="submit"
              disabled={disableSubmitButton}
            >
              {communicationPageBundle.getMessage(
                CorrespondenceTextEditorConstants.POST_CORRESPONDENCE,
              )}
            </KatButton>
          </EditorFooter>
        </ElementSection>
      </form>
    </TextEditingArea>
  );
};

export default TextEditor;
